import { useState, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { useProject } from '@/contexts/ProjectContext';
import { useVulnerabilitiesQuery } from '@/hooks/useVulnerabilitiesQuery';
import { useWriteBackLock } from '@/hooks/useWriteBackLock';
import { useReviewCommentsIndex } from '@/hooks/useReviewCommentsIndex';
import { queryKeys } from '@/lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Circle, Plus, Clock, Search } from 'lucide-react';
import { threatSeverity, type RatingSeverity } from '@/lib/owaspRating';
import { aiService } from '@/services/aiService';
import { threatcatService } from '@/services/threatcatService';
import { vulnerabilityService } from '@/services/vulnerabilityService';
import ThreatCard from './threats/ThreatCard';
import PendingVulnerabilityCard from './threats/PendingVulnerabilityCard';
import { toast } from 'sonner';
import ConnectModal from '@/components/threatcat/ConnectModal';
import { ThreatCatAuthError } from '@/lib/errors';
import { normalizeData } from '@/lib/parsers';
import type { Threat, ThreatStatus, ThreatType } from '@/types/threat';
import type { VulnerabilityOut } from '@/services/questionsService';
import { parsePhase, PHASES } from '@/lib/phaseUtils';

// Helper: convert VulnerabilityOut to Threat for ThreatCard display. Pure —
// safe to use both for rendering and for re-resolving a threat against a
// freshly-fetched vulnerability list.
const vulnToThreat = (v: VulnerabilityOut): Threat => ({
  id: v.threatcat_threat_id ?? v.id,
  name: v.name,
  vulnerability: v.vulnerability ?? undefined,
  scenario: v.scenario ?? undefined,
  countermeasure: v.countermeasure ?? undefined,
  rating_vector_string: v.rating_vector_string ?? undefined,
  status: (v.status as ThreatStatus) || 'open',
  status_note: v.status_note ?? undefined,
  type: (v.threat_type as ThreatType) || undefined,
  diagram_id: v.diagram_id ?? undefined,
});

// Mirrors ThreatCat's own ThreatStatusOptions enum (docs/threatcat-openapi.json)
// and backend/app/api/vulnerabilities.py's THREATCAT_STATUS_VALUES — a
// ThreatCat-backed threat can only ever be set to one of these.
const THREATCAT_STATUSES: ThreatStatus[] = ['open', 'mitigated', 'invalid'];

const SEVERITY_CARDS: { severity: RatingSeverity; label: string; border: string; dot: string; text: string }[] = [
  { severity: 'critical', label: 'Critical', border: 'border-red-200 dark:border-red-900', dot: 'fill-red-500 text-red-500', text: 'text-red-600' },
  { severity: 'high', label: 'High', border: 'border-orange-200 dark:border-orange-900', dot: 'fill-orange-500 text-orange-500', text: 'text-orange-600' },
  { severity: 'medium', label: 'Medium', border: 'border-yellow-200 dark:border-yellow-900', dot: 'fill-yellow-500 text-yellow-500', text: 'text-yellow-600' },
  { severity: 'low', label: 'Low', border: 'border-green-200 dark:border-green-900', dot: 'fill-green-500 text-green-500', text: 'text-green-600' },
  { severity: 'note', label: 'Note', border: 'border-sky-200 dark:border-sky-900', dot: 'fill-sky-500 text-sky-500', text: 'text-sky-600' },
  { severity: 'unrated', label: 'Unrated', border: 'border-gray-200 dark:border-gray-700', dot: 'fill-gray-400 text-gray-400', text: 'text-gray-600' },
];

export default function ThreatsTab() {
  const { threats, diagrams, projectInfo, setAppState } = useApp();
  const reviewIndex = useReviewCommentsIndex();
  const { cookie, disconnect, getCookie } = useThreatCat();
  const { projectId, detail } = useProject();
  const { isLocked: isWriteBackLocked, message: writeBackLockMessage } = useWriteBackLock();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [phaseFilter, setPhaseFilter] = useState<string>('all');
  const [enrichingIndex, setEnrichingIndex] = useState<number | null>(null);
  const [enrichingTcId, setEnrichingTcId] = useState<number | null>(null);
  const [showNewThreat, setShowNewThreat] = useState(false);
  const [enrichingNew, setEnrichingNew] = useState(false);
  const [pushingDraftId, setPushingDraftId] = useState<number | null>(null);
  const [enrichingDraftId, setEnrichingDraftId] = useState<number | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const pendingPushIdRef = useRef<number | null>(null);
  const pendingUpdateRef = useRef<{ vulnId: number; updated: Threat } | null>(null);

  const pid = projectId;
  const isConnectedMode = !!pid;
  const canEdit = !!pid && detail?.permission !== 'read_only';
  const { data: allVulns = [] } = useVulnerabilitiesQuery(pid);

  // Connected mode: split vulns by source (PRD-007)
  const tcVulns = useMemo(() => allVulns.filter((v) => v.source === 'threatcat' && v.status !== 'removed_from_tc'), [allVulns]);
  const drafts = useMemo(() => allVulns.filter((v) => v.source !== 'threatcat' && v.status !== 'removed_from_tc'), [allVulns]);

  const executeSaveConnectedThreat = useCallback(async (vulnId: number, updated: Threat, activeCookie: string) => {
    if (isWriteBackLocked) {
      if (writeBackLockMessage) toast.error(writeBackLockMessage);
      return;
    }
    const versionId = detail?.project_version_id;
    if (!versionId) {
      toast.error('Connect to ThreatCat first to update threats');
      return;
    }
    try {
      const result = await vulnerabilityService.update(
        vulnId,
        {
          name: updated.name,
          vulnerability: updated.vulnerability ?? undefined,
          scenario: updated.scenario ?? undefined,
          countermeasure: updated.countermeasure ?? undefined,
          rating_vector_string: updated.rating_vector_string ?? null,
          status: updated.status ?? undefined,
          status_note: updated.status_note ?? undefined,
        },
        { cookie: activeCookie, projectVersionId: String(versionId) },
      );
      if (pid) queryClient.setQueryData<VulnerabilityOut[]>(queryKeys.vulnerabilities.all(pid), (prev) => prev?.map((v) => (v.id === vulnId ? result : v)));
      if (result.tc_synced !== true) {
        toast.error('Saved locally but failed to sync to ThreatCat');
      } else {
        toast.success('Threat updated');
      }
    } catch (err) {
      if (err instanceof ThreatCatAuthError) disconnect();
      toast.error(err instanceof Error ? err.message : 'Failed to update threat');
    }
  }, [detail?.project_version_id, disconnect, isWriteBackLocked, pid, queryClient, writeBackLockMessage]);

  // Connected mode: save TC threat directly to ThreatCat and local cache
  const handleSaveConnectedThreat = useCallback(async (vulnId: number, _original: Threat, updated: Threat) => {
    if (isWriteBackLocked) {
      if (writeBackLockMessage) toast.error(writeBackLockMessage);
      return;
    }
    if (!cookie || !detail?.project_version_id) {
      pendingUpdateRef.current = { vulnId, updated };
      setConnectOpen(true);
      return;
    }
    await executeSaveConnectedThreat(vulnId, updated, cookie);
  }, [cookie, detail?.project_version_id, executeSaveConnectedThreat, isWriteBackLocked, writeBackLockMessage]);

  const executePushDraft = useCallback(async (id: number, activeCookie: string) => {
    if (isWriteBackLocked) {
      if (writeBackLockMessage) toast.error(writeBackLockMessage);
      return;
    }
    const versionId = detail?.project_version_id;
    if (!versionId) {
      toast.error('Connect to ThreatCat first to push threats');
      return;
    }
    setPushingDraftId(id);
    try {
      await vulnerabilityService.push(id, activeCookie, String(versionId));
      if (pid) {
        try {
          const exportData = await threatcatService.fetchThreatCatProjectExport(activeCookie, versionId, pid);
          setAppState({ threats: normalizeData(exportData).threats });
        } catch (syncErr) {
          if (syncErr instanceof ThreatCatAuthError) disconnect();
          console.error('Failed to refresh project export after threat push:', syncErr);
        }
        queryClient.invalidateQueries({ queryKey: queryKeys.vulnerabilities.all(pid) });
      }
      toast.success('Threat pushed to ThreatCat');
    } catch (err) {
      if (err instanceof ThreatCatAuthError) disconnect();
      toast.error(err instanceof Error ? err.message : 'Failed to push threat');
    } finally {
      setPushingDraftId(null);
    }
  }, [detail?.project_version_id, disconnect, isWriteBackLocked, pid, queryClient, setAppState, writeBackLockMessage]);

  const handlePushDraft = useCallback((id: number) => {
    if (isWriteBackLocked) {
      if (writeBackLockMessage) toast.error(writeBackLockMessage);
      return;
    }
    if (!cookie) {
      pendingPushIdRef.current = id;
      setConnectOpen(true);
      return;
    }
    void executePushDraft(id, cookie);
  }, [cookie, executePushDraft, isWriteBackLocked, writeBackLockMessage]);

  const handleConnected = useCallback(() => {
    const nextCookie = getCookie();
    const id = pendingPushIdRef.current;
    pendingPushIdRef.current = null;
    const pendingUpdate = pendingUpdateRef.current;
    pendingUpdateRef.current = null;

    if (isWriteBackLocked) return;

    if (id != null && nextCookie) {
      void executePushDraft(id, nextCookie);
    } else if (pendingUpdate && nextCookie) {
      void executeSaveConnectedThreat(pendingUpdate.vulnId, pendingUpdate.updated, nextCookie);
    }
  }, [executePushDraft, executeSaveConnectedThreat, getCookie, isWriteBackLocked]);

  const handleConnectionClose = useCallback(() => {
    setConnectOpen(false);
    pendingPushIdRef.current = null;
    pendingUpdateRef.current = null;
  }, []);

  // Keep the loaded snapshot available for local presentation.
  const handleSaveThreat = useCallback(async (index: number, updated: Threat) => {
    const newThreats = [...threats];
    newThreats[index] = updated;
    setAppState({ threats: newThreats });
  }, [threats, setAppState]);

  // Build enrichment context from the appropriate data source
  const buildEnrichmentContext = useCallback((threat: Threat) => {
    const diagram = diagrams.find((d) => d.id === threat.diagram_id) ?? diagrams[0];
    const contextThreats = isConnectedMode
      ? allVulns.map((v) => ({
          name: v.name,
          scenario: v.scenario ?? undefined,
          vulnerability: v.vulnerability ?? undefined,
          countermeasure: v.countermeasure ?? undefined,
          status: v.status as ThreatStatus,
          rating_vector_string: v.rating_vector_string ?? undefined,
        }))
      : threats.map((t) => ({
          name: t.name,
          scenario: t.scenario,
          vulnerability: t.vulnerability,
          countermeasure: t.countermeasure,
          status: t.status,
          rating_vector_string: t.rating_vector_string,
        }));
    return {
      entities: diagram?.entities ?? [],
      paths: diagram?.paths ?? [],
      threats: contextThreats,
      project_info: projectInfo ? { name: projectInfo.name, description: projectInfo.description } : undefined,
    };
  }, [diagrams, threats, allVulns, isConnectedMode, projectInfo]);

  // Enrich a TC threat (connected mode)
  const handleEnrichConnectedThreat = useCallback(async (vulnId: number, threat: Threat): Promise<Record<string, string>> => {
    setEnrichingTcId(vulnId);
    try {
      const diagramData = buildEnrichmentContext(threat);
      const fieldsToGenerate = ['vulnerability', 'scenario', 'countermeasure', 'rating_vector_string'];
      return await aiService.enrichThreat(threat as unknown as Record<string, unknown>, diagramData, fieldsToGenerate);
    } finally {
      setEnrichingTcId(null);
    }
  }, [buildEnrichmentContext]);

  // Enrich a threat (offline mode)
  const handleEnrichWithAI = useCallback(async (threat: Threat, index: number): Promise<Record<string, string>> => {
    setEnrichingIndex(index);
    try {
      const diagramData = buildEnrichmentContext(threat);
      const fieldsToGenerate = ['vulnerability', 'scenario', 'countermeasure', 'rating_vector_string'];
      return await aiService.enrichThreat(threat as unknown as Record<string, unknown>, diagramData, fieldsToGenerate);
    } finally {
      setEnrichingIndex(null);
    }
  }, [buildEnrichmentContext]);

  const handleAddThreat = useCallback(async (draft: Threat) => {
    if (pid) {
      try {
        const created = await vulnerabilityService.create(pid, {
          name: draft.name,
          vulnerability: draft.vulnerability ?? undefined,
          scenario: draft.scenario ?? undefined,
          countermeasure: draft.countermeasure ?? undefined,
          rating_vector_string: draft.rating_vector_string ?? undefined,
        });
        queryClient.setQueryData<VulnerabilityOut[]>(queryKeys.vulnerabilities.all(pid), (prev) => [created, ...(prev ?? [])]);
        setShowNewThreat(false);
        toast.success('Vulnerability saved');
        return;
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to save vulnerability');
        return;
      }
    }
    setAppState({ threats: [...threats, draft] });
    setShowNewThreat(false);
  }, [pid, threats, setAppState, queryClient]);

  const handleSaveDraft = useCallback(async (id: number, updated: Partial<VulnerabilityOut>) => {
    try {
      const sanitized = {
        name: updated.name,
        vulnerability: updated.vulnerability ?? undefined,
        scenario: updated.scenario ?? undefined,
        countermeasure: updated.countermeasure ?? undefined,
        rating_vector_string: updated.rating_vector_string ?? null,
      };
      const result = await vulnerabilityService.update(id, sanitized);
      if (pid) queryClient.setQueryData<VulnerabilityOut[]>(queryKeys.vulnerabilities.all(pid), (prev) => prev?.map((v) => (v.id === id ? result : v)));
      toast.success('Vulnerability updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update vulnerability');
    }
  }, []);

  const handleDiscardDraft = useCallback(async (id: number) => {
    try {
      await vulnerabilityService.remove(id);
      if (pid) queryClient.setQueryData<VulnerabilityOut[]>(queryKeys.vulnerabilities.all(pid), (prev) => prev?.filter((v) => v.id !== id));
      toast.success('Vulnerability discarded');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to discard vulnerability');
    }
  }, []);

  const handleEnrichDraft = useCallback(async (draft: VulnerabilityOut): Promise<Record<string, string>> => {
    setEnrichingDraftId(draft.id);
    try {
      const asThreat: Threat = { name: draft.name, status: (draft.status as ThreatStatus) || 'open' };
      const diagramData = buildEnrichmentContext(asThreat);
      const fieldsToGenerate = ['vulnerability', 'scenario', 'countermeasure', 'rating_vector_string'];
      return await aiService.enrichThreat(draft as unknown as Record<string, unknown>, diagramData, fieldsToGenerate);
    } finally {
      setEnrichingDraftId(null);
    }
  }, [buildEnrichmentContext]);

  const handleEnrichNew = useCallback(async (threat: Threat): Promise<Record<string, string>> => {
    setEnrichingNew(true);
    try {
      const diagramData = buildEnrichmentContext(threat);
      const fieldsToGenerate = ['vulnerability', 'scenario', 'countermeasure', 'rating_vector_string'];
      return await aiService.enrichThreat(threat as unknown as Record<string, unknown>, diagramData, fieldsToGenerate);
    } finally {
      setEnrichingNew(false);
    }
  }, [buildEnrichmentContext]);

  const matchesSeverity = (vector: string | null | undefined, threatType: string | null | undefined) =>
    severityFilter === 'all' || threatSeverity(vector, threatType) === severityFilter;

  const matchesSearch = (name: string, extId?: string | null) => {
    if (!searchQuery.trim()) return true;
    const lq = searchQuery.toLowerCase();
    return name.toLowerCase().includes(lq) || (extId ?? '').toLowerCase().includes(lq);
  };

  const matchesPhase = (extId?: string | null) => {
    if (phaseFilter === 'all') return true;
    return parsePhase(extId) === phaseFilter;
  };

  const showThreatCat = statusFilter === 'all' || statusFilter !== 'pending';
  const showPending = statusFilter === 'all' || statusFilter === 'pending';

  // In connected mode, use tcVulns for TC stats; in offline mode, use in-memory threats
  const mainThreats = isConnectedMode ? tcVulns : threats;

  const filteredTcVulns = isConnectedMode
    ? tcVulns.filter((v) => {
        if (statusFilter === 'pending') return false;
        const statusMatch = statusFilter === 'all' || v.status === statusFilter;
        return statusMatch && matchesSeverity(v.rating_vector_string, v.threat_type) && matchesSearch(v.name, v.external_id) && matchesPhase(v.external_id);
      })
    : [];

  const filteredSnapshotThreats = !isConnectedMode
    ? threats.filter((threat) => {
        if (statusFilter === 'pending') return false;
        const statusMatch = statusFilter === 'all' || threat.status === statusFilter;
        return statusMatch && matchesSeverity(threat.rating_vector_string, threat.type) && matchesSearch(threat.name, undefined) && matchesPhase(undefined);
      })
    : [];

  const filteredThreatsCount = isConnectedMode ? filteredTcVulns.length : filteredSnapshotThreats.length;

  const filteredDrafts = drafts.filter((draft) => {
    if (statusFilter !== 'all' && statusFilter !== 'pending') return false;
    return matchesSeverity(draft.rating_vector_string, draft.threat_type) && matchesSearch(draft.name, draft.external_id) && matchesPhase(draft.external_id);
  });

  const totalCount = mainThreats.length + drafts.length;

  // Count threats by status
  const statusCounts = mainThreats.reduce((acc, item) => {
    const st = 'source' in item ? (item as VulnerabilityOut).status : (item as Threat).status;
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const pendingCount = drafts.length;

  // Count threats by OWASP Risk Rating severity
  const countBySeverity = (items: { vector?: string | null; type?: string | null }[]) =>
    items.reduce((acc, { vector, type }) => {
      const severity = threatSeverity(vector, type);
      acc[severity] = (acc[severity] || 0) + 1;
      return acc;
    }, {} as Partial<Record<RatingSeverity, number>>);

  const severityCounts = countBySeverity(mainThreats.map((item) => ('source' in item
    ? { vector: (item as VulnerabilityOut).rating_vector_string, type: (item as VulnerabilityOut).threat_type }
    : { vector: (item as Threat).rating_vector_string, type: (item as Threat).type })));
  const draftSeverityCounts = countBySeverity(drafts.map((d) => ({ vector: d.rating_vector_string, type: d.threat_type })));

  const hasSeverityData = totalCount > 0;

  return (
    <div className="space-y-6">
      <ConnectModal
        open={connectOpen}
        onClose={handleConnectionClose}
        onConnected={handleConnected}
      />
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card className={statusFilter === 'all' && severityFilter === 'all' ? 'ring-2 ring-primary' : 'cursor-pointer hover:ring-1 hover:ring-primary/50'} onClick={() => { setStatusFilter('all'); setSeverityFilter('all'); }}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCount}</div>
          </CardContent>
        </Card>

        <Card className={statusFilter === 'open' ? 'ring-2 ring-primary' : 'cursor-pointer hover:ring-1 hover:ring-primary/50'} onClick={() => setStatusFilter('open')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{statusCounts.open || 0}</div>
          </CardContent>
        </Card>

        <Card className={statusFilter === 'mitigated' ? 'ring-2 ring-primary' : 'cursor-pointer hover:ring-1 hover:ring-primary/50'} onClick={() => setStatusFilter('mitigated')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mitigated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{statusCounts.mitigated || 0}</div>
          </CardContent>
        </Card>

        <Card className={statusFilter === 'invalid' ? 'ring-2 ring-primary' : 'cursor-pointer hover:ring-1 hover:ring-primary/50'} onClick={() => setStatusFilter('invalid')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Invalid</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">{statusCounts.invalid || 0}</div>
          </CardContent>
        </Card>

        <Card className={statusFilter === 'pending' ? 'ring-2 ring-primary' : 'cursor-pointer hover:ring-1 hover:ring-primary/50'} onClick={() => setStatusFilter('pending')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{pendingCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* OWASP Risk Rating severity cards */}
      {hasSeverityData && (
        <div className="grid gap-4 grid-cols-2 md:grid-cols-6">
          {SEVERITY_CARDS.map(({ severity, label, border, dot, text }) => (
            <Card
              key={severity}
              className={`${border} ${severityFilter === severity ? 'ring-2 ring-primary' : 'cursor-pointer hover:ring-1 hover:ring-primary/50'}`}
              onClick={() => setSeverityFilter(severityFilter === severity ? 'all' : severity)}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-1.5"><Circle className={`h-3 w-3 ${dot}`} />{label}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${text}`}>
                  {severityCounts[severity] || 0}
                  {draftSeverityCounts[severity] ? <span className="text-sm font-normal text-muted-foreground ml-1">+{draftSeverityCounts[severity]}</span> : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Search + Phase filter row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search threats, IDs…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Select value={phaseFilter} onValueChange={setPhaseFilter}>
          <SelectTrigger className="h-9 w-full sm:w-44">
            <SelectValue placeholder="All phases" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All phases</SelectItem>
            {PHASES.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
            <SelectItem value="OTHER">Other</SelectItem>
            <SelectItem value="NONE">Uncategorized</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Action row */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {filteredThreatsCount + (showPending ? filteredDrafts.length : 0)} of {totalCount} threats
        </p>
        {canEdit && (
          <Button size="sm" onClick={() => setShowNewThreat(true)} disabled={showNewThreat}>
            <Plus className="h-4 w-4 mr-1" />Add Threat
          </Button>
        )}
      </div>

      {/* New Threat inline form */}
      {showNewThreat && (
        <ThreatCard
          key="new-threat"
          threat={{ name: '', status: 'open' }}
          index={-1}
          canEdit={canEdit}
          onSave={() => {}}
          onEnrichWithAI={handleEnrichNew}
          isEnriching={enrichingNew}
          isNew
          onAdd={handleAddThreat}
          onCancelAdd={() => setShowNewThreat(false)}
        />
      )}

      {/* Draft Threats (Pending) */}
      {showPending && filteredDrafts.length > 0 && (
        <div className="space-y-4">
          {filteredDrafts.map((draft) => (
            <PendingVulnerabilityCard
              key={`draft-${draft.id}`}
              draft={draft}
              canEdit={canEdit}
              onSave={handleSaveDraft}
              onPush={handlePushDraft}
              onDiscard={handleDiscardDraft}
              isPushing={pushingDraftId === draft.id}
              pushDisabledReason={writeBackLockMessage}
              onEnrichWithAI={handleEnrichDraft}
              isEnriching={enrichingDraftId === draft.id}
            />
          ))}
        </div>
      )}

      {/* ThreatCat threats list / snapshot threats */}
      {showThreatCat && (
        <div className="space-y-4">
          {filteredThreatsCount === 0 && filteredDrafts.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">No threats match the current filters.</p>
              </CardContent>
            </Card>
          ) : isConnectedMode ? (
            filteredTcVulns.map((vuln) => {
              const threat = vulnToThreat(vuln);
              const diagram = vuln.diagram_id ? diagrams.find((d) => d.id === vuln.diagram_id) : undefined;
              const tcThreatId = vuln.threatcat_threat_id ?? vuln.id;
              const threatComments = reviewIndex.getThreatComments(tcThreatId);

              return (
                <ThreatCard
                  key={vuln.id}
                  threat={threat}
                  index={vuln.id}
                  diagramName={diagram?.name}
                  externalId={vuln.external_id}
                  canEdit={canEdit}
                  editDisabledReason={writeBackLockMessage}
                  onSave={(_, updated) => handleSaveConnectedThreat(vuln.id, threat, updated)}
                  onEnrichWithAI={(t) => handleEnrichConnectedThreat(vuln.id, t)}
                  isEnriching={enrichingTcId === vuln.id}
                  allowedStatuses={THREATCAT_STATUSES}
                  reviewComments={threatComments}
                />
              );
            })
          ) : (
            filteredSnapshotThreats.map((threat) => {
              const originalIndex = threats.indexOf(threat);
              const diagram = threat.diagram_id ? diagrams.find((d) => d.id === threat.diagram_id) : undefined;
              const threatComments = typeof threat.id === 'number'
                ? reviewIndex.getThreatComments(threat.id)
                : [];

              return (
                <ThreatCard
                  key={originalIndex}
                  threat={threat}
                  index={originalIndex}
                  diagramName={diagram?.name}
                  canEdit
                  onSave={handleSaveThreat}
                  onEnrichWithAI={(t) => handleEnrichWithAI(t, originalIndex)}
                  isEnriching={enrichingIndex === originalIndex}
                  reviewComments={threatComments}
                />
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
